import React, {useState} from "react";
import { deepCopyObj } from "../utils/object";

export const DataTablePreferenceContext = new React.createContext();

const STORAGE_KEY = 'datatable_preferences';
// Bump when the durable shape changes so stale stored data is discarded.
const STORAGE_VERSION = 1;
// Only "display" preferences are persisted durably (localStorage). Page position
// (offset), search keyword, and filters stay in-memory for the session, so a reload
// or a fresh sign-in lands on page 1 with a clean filter set, while the user's chosen
// page size and sort column are remembered across reloads and sign-ins.
const DURABLE_KEYS = ['limit', 'sort'];

const isPlainObject = (val) => val !== null && typeof val === 'object' && !Array.isArray(val);

// Pull only the durable keys (limit/sort) out of the live preference tree.
const extractDurable = (node) => {
    const result = {};
    Object.entries(node).forEach(([key, value]) => {
        if (DURABLE_KEYS.includes(key)) {
            if (value !== undefined) result[key] = value;
        } else if (isPlainObject(value)) {
            const nested = extractDurable(value);
            if (Object.keys(nested).length > 0) result[key] = nested;
        }
    });
    return result;
};

// Overlay persisted durable values onto a fresh copy of initialState (mutates base).
const applyDurable = (base, stored) => {
    if (!isPlainObject(base) || !isPlainObject(stored)) return;
    Object.entries(stored).forEach(([key, value]) => {
        if (DURABLE_KEYS.includes(key)) {
            base[key] = value;
        } else if (isPlainObject(value) && isPlainObject(base[key])) {
            applyDurable(base[key], value);
        }
    });
};

const initialState = {
    payouts: { 
        sort: { direction: 'desc', col: 'payout_date'} ,
        offset: 0,
        dateRange: undefined,
        amountRange: undefined
    },
    invoices: {
        unpaid: {
            sort: { col: 'issued_at', direction: 'desc' },
            filters: ['not_yet_due', 'overdue'],
            dateRange: undefined,
            amountRange: undefined,
            keyword: '',
            offset: 0,
            limit: undefined
        },
        paid: {
            sort: { col: 'issued_at', direction: 'desc' },
            filters: ['paid','voided','has_credit'],
            dateRange: undefined,
            amountRange: undefined,
            keyword: '',
            offset: 0,
            limit: undefined
        },
        all: { 
            sort: { col: 'issued_at', direction: 'desc' },
            filters: [],
            dateRange: undefined,
            amountRange: undefined,
            keyword: '',
            offset: 0,
            limit: undefined
        },
        scheduled: { 
            sort: { direction: 'asc', col: 'issued_at'},
            dateRange: undefined,
            amountRange: undefined,
            keyword: '',
            offset: 0,
            limit: undefined
        },
        recurring: { 
            sort: { direction: 'desc', col: 'issued_at'},
            dateRange: undefined,
            amountRange: undefined,
            keyword: '',
            offset: 0,
            limit: undefined
        },
        draft: { 
            sort: { direction: 'desc', col: 'updated_at'},
            dateRange: undefined,
            amountRange: undefined,
            keyword: '',
            offset: 0,
            limit: undefined
        },
        failed: {
            sort: { direction: 'desc', col: 'updated_at'}
        },
        // The invoice list embedded in a customer / customer group profile. It renders
        // as a "tab: all" invoice list, but keeps its own bucket so sorting there is
        // remembered on its own terms instead of re-sorting the top-level invoice list.
        customer_profile: {
            sort: { col: 'issued_at', direction: 'desc' },
            filters: [],
            dateRange: undefined,
            amountRange: undefined,
            keyword: '',
            offset: 0,
            limit: undefined
        },
        activeTab: ''
    },
    customers: {
        individuals: {
            sort: { direction: 'desc', col: 'last_invoiced_at'},
            keyword: '',
            offset: 0,
            limit: undefined
        },
        groups: {
            sort: { direction: 'desc', col: 'last_invoiced_at'},
            keyword: '',
            offset: 0,
            limit: undefined
        }
    },
    products: {
        sort: { direction: 'desc', col: 'name'},
        keyword: '',
        offset: 0,
        limit: undefined
    },
    // Keyed by the exact `dataName` the deposits list passes. Every table that
    // passes a dataName needs a bucket here: DataTableWithBackendSearch reads
    // this tree before first render and destructures the result, and
    // updatePreferences writes into it on every page change, so a missing bucket
    // is a crash, not a silently-unremembered preference. The dash (rather than
    // the snake_case the older keys use) matches the string in
    // pages/deposits/index.js and is only ever compared against it.
    'deposit-links': {
        sort: { direction: 'desc', col: 'created_at'},
        filters: [],
        dateRange: undefined,
        amountRange: undefined,
        keyword: '',
        offset: 0,
        limit: undefined
    },
    transactions: {
        sort: { direction: 'desc', col: 'created_at'},
        filters: [],
        dateRange: undefined,
        amountRange: undefined,
        keyword: '',
        offset: 0,
        limit: undefined
    },
    audit_logs: {
        sort: { direction: 'desc', col: 'created_at'},
        dateRange: undefined,
        offset: 0,
        limit: undefined
    },
    billing_statements: {
        sort: { direction: 'desc', col: 'billed_on'},
        offset: 0,
        limit: undefined
    },
};

// Build the starting preference tree: defaults overlaid with the durable
// (page size / sort) values saved in localStorage. Falls back to defaults on the
// server, when nothing is stored, or when the stored data is stale/corrupt.
const loadPreferences = () => {
    const base = deepCopyObj(initialState); // need a fresh obj
    if (typeof window === 'undefined') return base;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return base;
        const parsed = JSON.parse(raw);
        if (parsed && parsed.version === STORAGE_VERSION) {
            applyDurable(base, parsed.preferences || {});
        }
    } catch (e) {
        // Ignore unavailable/corrupt storage and fall back to defaults
    }
    return base;
};

const savePreferences = (preferences) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
            version: STORAGE_VERSION,
            preferences: extractDurable(preferences),
        }));
    } catch (e) {
        // Storage may be unavailable (private mode / quota) - non-fatal
    }
};

const DataTablePreferenceProvider = ({ children }) => {
    //default state of each data table, hydrated with durable prefs from localStorage
    const [preferences, setPreferences] = useState(loadPreferences);

    const updatePreferences = ({table, tab, key, value}) => {
        if (!table) return undefined;
        if (tab) {
            preferences[table][tab][key] = value;
        } else {
            preferences[table][key] = value;
        }
        // Only page size / sort are persisted across reloads and sign-ins.
        if (DURABLE_KEYS.includes(key)) savePreferences(preferences);
    }

    const getPreference = ({table, tab, key}) => {
        if (table && !tab && !key) return preferences[table];
        if (table && !key) return preferences[table][tab];
        return tab ? preferences[table][tab][key] : preferences[table][key];
    }

    // Reset session state (page, search, filters) while keeping the durable
    // display prefs (page size / sort) restored from localStorage.
    const resetPreference = () => setPreferences(loadPreferences());

    return (
        <DataTablePreferenceContext.Provider value={{
            preferences,
            updatePreferences,
            getPreference,
            resetPreference
        }}>
            {children}
        </DataTablePreferenceContext.Provider>
    )
}

export default DataTablePreferenceProvider;